import { create } from 'zustand';
import { User, UserRole } from '../types/models';
import { userApi } from '../api/userApi';

interface CreateUserData {
  email: string;
  full_name: string;
  password: string;
  roles: UserRole[];
  is_active: boolean;
}

interface UpdateUserData {
  email?: string;
  full_name?: string;
  password?: string;
  roles?: UserRole[];
  is_active?: boolean;
}

interface UserStore {
  users: User[];
  isLoading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  fetchActiveUsers: () => Promise<User[]>;
  createUser: (data: CreateUserData) => Promise<void>;
  updateUser: (id: number, data: UpdateUserData) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
}

export const useUserStore = create<UserStore>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,

  fetchUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const users = await userApi.getUsers();
      set({ users, error: null });
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch users' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchActiveUsers: async () => {
    try {
      const users = await userApi.getUsers();
      const activeUsers = users.filter(user => user.is_active);
      return activeUsers;
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to fetch active users' });
      return [];
    }
  },

  createUser: async (data) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.createUser(data);
      await get().fetchUsers();
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to create user' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  updateUser: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.updateUser(id, data);
      await get().fetchUsers();
    } catch (error: any) {
      set({ error: error.response?.data?.detail || 'Failed to update user' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  deleteUser: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await userApi.deleteUser(id);
      await get().fetchUsers();
    } catch (error: any) {
      // Store the error message in the state
      const errorMessage = error.response?.data?.detail || 'Failed to delete user';
      set({ error: errorMessage });
      
      // Rethrow with the detailed error message to ensure it's available to components
      if (error.response?.data?.detail) {
        error.message = error.response.data.detail;
      }
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },
}));
